import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ConfirmationDialog from '../components/ConfirmationDialog';
import DermoPlaintForm from './DermoPlaintForm';

const DermoPlaints = () => {
  const [plaints, setPlaints] = useState([]);
  const [filteredPlaints, setFilteredPlaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [currentPlaint, setCurrentPlaint] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [plaintToDelete, setPlaintToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const authHeaders = useMemo(
    () => ({
      'x-access-token': localStorage.getItem('token') || sessionStorage.getItem('token'),
      Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
    }),
    []
  );

  const fetchPlaints = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/complaints', { headers: authHeaders });
      setPlaints(response.data);
      setFilteredPlaints(response.data);
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.message || 'Failed to fetch dermo plaints');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlaints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredPlaints(plaints);
      return;
    }

    const q = searchQuery.toLowerCase();
    setFilteredPlaints(
      plaints.filter((p) => (p.plaint || '').toLowerCase().includes(q))
    );
  }, [searchQuery, plaints]);

  const handleAddClick = () => {
    setCurrentPlaint(null);
    setOpen(true);
  };

  const handleUpdateClick = (plaint) => {
    setCurrentPlaint(plaint);
    setOpen(true);
  };

  const handleDeleteClick = (id) => {
    setPlaintToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteDialogOpen(false);
    if (!plaintToDelete) return;

    setPlaints((prev) => prev.filter((p) => p._id !== plaintToDelete));
    setFilteredPlaints((prev) => prev.filter((p) => p._id !== plaintToDelete));

    try {
      await axios.delete(`/complaints/${plaintToDelete}`, { headers: authHeaders });
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.message || 'Failed to delete dermo plaint');
      fetchPlaints();
    } finally {
      setPlaintToDelete(null);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentPlaint(null);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box padding={2}>
      <Typography variant="h4" gutterBottom>
        Dermo Plaints
      </Typography>

      <Button variant="contained" color="primary" onClick={handleAddClick} sx={{ mb: 2 }}>
        Add Dermo Plaint
      </Button>

      <TextField
        label="Search"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchQuery}
        onChange={handleSearchChange}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Plaint</TableCell>
                <TableCell>Associated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlaints
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((p) => (
                  <TableRow key={p._id}>
                    <TableCell>{p.plaint}</TableCell>
                    <TableCell>{Array.isArray(p.associatedPlaintes) ? p.associatedPlaintes.join(', ') : ''}</TableCell>
                    <TableCell align="right">
                      <ButtonGroup variant="outlined" size="small">
                        <Button onClick={() => handleUpdateClick(p)} color="primary">
                          Update
                        </Button>
                        <Button onClick={() => handleDeleteClick(p._id)} color="error">
                          Delete
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={filteredPlaints.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message="Are you sure you want to delete this dermo plaint?"
        confirmText="Delete"
        confirmColor="error"
      />

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>{currentPlaint ? 'Update Dermo Plaint' : 'Add Dermo Plaint'}</DialogTitle>
        <DialogContent>
          <DermoPlaintForm plaint={currentPlaint} onClose={handleClose} onSave={fetchPlaints} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DermoPlaints;
